import React, { useState, useEffect, ChangeEvent, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import Select, { MultiValue } from 'react-select';
import { Form } from 'react-bootstrap';
import secureLocalStorage from 'react-secure-storage';

import { get_single_user, get_tenant_setting, update_tenant_user, update_user } from '../api/usersApi';
import { filter_role_by_status } from '../roles/api/rolesApi';
import { validateImage } from '../../../helper-functions/ImageValidation';
import { get_all_active_categories } from '../../categories/api/categoriesApi';
import { get_all_meeting_locations, get_all_virtual_platforms } from '../../locations/api/locationsApi';
import { getCountries } from '../../../helper-functions/Countries';
import { get_all_active_subcategories, get_all_subcategories_against_category } from '../../subcategories/api/subcategoriesApi';

// Interfaces
interface userroleList {
  id: number;
  name: string;
}
interface Category {
  id: number;
  title: string;
}
interface Currency {
  currency_name: string;
  currency_symbol: string;
}
interface SubCategory {
  id?: number;
  people_id?: number;
  categories_id?: number;
  title?: string;
  people_title?: string;
  sub_category_id: number;
  sub_category_title: string;
}
interface PeopleLocation {
  id?: number;
  people_id?: number;
  people_title?: string;
  location_id: number;
  type?: string;
  platform_name: string;
}
interface PeoplePlatform {
  id?: number;
  people_id?: number;
  people_title?: string;
  location_id: number;
  type?: string;
  platform_name: string;
}
interface PeopleDetails {
  id: number;
  address_line_2: string;
  town: string;
  city: string;
  postal_code: string;
  country: string;
  other_information: string;
  status: number;
  appointment_rate: number;
  currency: Currency;
  sub_categories: SubCategory[];
  people_locations: PeopleLocation[];
  people_platforms: PeoplePlatform[];
  selected_subcategory_ids: Record<string, any>;
  selected_category_ids: Record<string, any>;
  active_categories_data: Record<string, any>;
  active_subcategories_against_categories: Record<string, any>;
}
interface AllPeople {
  id: number;
  department_id: number | null;
  department_name: string | null;
  title: string;
  firstname: string;
  middlename: string;
  lastname: string;
  fullname: string;
  email: string;
  role_id: number;
  role: string;
  mobile: string;
  telephone: string;
  picture: string | null;
  status: number;
  address: string;
  people_details: PeopleDetails;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  updated_by: number;
  updated_by_name: string;
  deleted_by: number | null;
  deleted_by_name: string | null;
}
// Dropdown option type for React Select
interface SelectOption {
  value: string;
  label: string;
}

function EditUser() {
  const [selectedUser, setSelectedUser] = useState<AllPeople | null>(null);
  const [userPicture, setUserPicture] = useState<File | null>(null);
  const [roleList, setRoleList] = useState<userroleList[]>([]);
  const [firstNameError, setFirstNameError] = useState('');
  const [titleError, setTitleError] = useState('');
  const [roleError, setRoleError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [mobileValidationError, setMobileValidationError] = useState('');
  const [telephoneValidationError, setTelephoneValidationError] = useState('');
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<PeopleLocation[]>([]);
  const [platforms, setPlatforms] = useState<PeoplePlatform[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategoryValidationError, setSubcategoryValidationError] = useState('');
  const [townError, setTownError] = useState('');
  const [appointmentRateError, setAppointmentRateError] = useState('');
  const [cityError, setCityError] = useState('');
  const [countryError, setCountryError] = useState('');
  const [postCodeError, setPostCodeError] = useState('');
  const location = useLocation();
  const id = (location.state as { user: AllPeople }).user?.id;
  const { data: viewUser, isLoading: viewIsLoading, error: viewIsError, refetch } = useQuery(
    ['get_single_user', id],
    () => get_single_user(id),
    {
      staleTime: 0,
      cacheTime: 0,
      refetchOnWindowFocus: true,
    }
  );
  const [allSubcategories, setAllSubcategories] = useState<SubCategory[]>([]);

const { data: allSubcategoriesData } = useQuery(
    'all_active_subcategories', 
    get_all_active_subcategories, 
    { refetchOnWindowFocus: false }
);
useEffect(() => {
    if (allSubcategoriesData?.data) {
        setAllSubcategories(allSubcategoriesData.data);
    }
}, [allSubcategoriesData]);
  useEffect(() => {
    refetch();
  }, [id, refetch]);
  useEffect(() => {
    if (viewUser) {
      setSelectedUser(viewUser.data[0]);
    }
  }, [viewUser]);
  const { data: roleData } = useQuery(
    'filter_role_by_status',
    () => filter_role_by_status(1),
    { refetchOnWindowFocus: false }
  );
  const [organisationApplicationData, setOrganisationApplicationData] = useState({
    application_id: 0,
    organization_id: 0,
  });
  useEffect(() => {
    if (roleData) {
      setRoleList(roleData.data);
    }
  }, [roleData]);
  useEffect(() => {
    const fetchTenantSetting = async () => {
      try {
        const result = await get_tenant_setting();
        setOrganisationApplicationData(result);
      } catch (error) {
        console.error('Error fetching tenant settings:', error);
      }
    };
    fetchTenantSetting();
  }, []);
  const filteredSubcategories = useMemo(() => {
    
    const selectedCategories = selectedUser?.people_details?.selected_category_ids;
    const selectedCategoryIds = selectedCategories 
      ? Object.values(selectedCategories).map(c => c.id) 
      : [];

  
    if (selectedCategoryIds.length === 0) {
        return [];
    }
    
  
    return allSubcategories.filter(subcategory => 
      
        selectedCategoryIds.includes(subcategory.categories_id) 
    );
}, [selectedUser?.people_details?.selected_category_ids, allSubcategories]);
  const handleFirstNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const newFirstName = e.target.value;
      if (!newFirstName) {
        setFirstNameError('FirstName is required.');
      } else {
        setFirstNameError('');
      }
      setSelectedUser({
        ...selectedUser,
        firstname: newFirstName,
      });
    }
  };
  const handleTitleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (selectedUser) {
      const newTitleName = e.target.value;
      if (!newTitleName) {
        setTitleError('Title is required.');
      } else {
        setTitleError('');
      }
      setSelectedUser({
        ...selectedUser,
        title: newTitleName,
      });
    }
  };
  const handleAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const addressNew = e.target.value;
      setSelectedUser({
        ...selectedUser,
        address: addressNew,
      });
    }
  };
  const handleMidddleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const middleName = e.target.value;
      setSelectedUser({
        ...selectedUser,
        middlename: middleName,
      });
    }
  };
  const handlEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const email = e.target.value;
      if (!email) {
        setEmailError('Email is required.');
      } else {
        setEmailError('');
      }
      setSelectedUser({
        ...selectedUser,
        email: email,
      });
    }
  };
  const handleLastNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const lastName = e.target.value;
      setSelectedUser({
        ...selectedUser,
        lastname: lastName,
      });
    }
  };
  const handleTelephoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const newUserTelephone = e.target.value;
      setSelectedUser({
        ...selectedUser,
        telephone: newUserTelephone,
      });
      if (newUserTelephone) {
        const numberRegex = /^[+0-9]+$/;
        if (!numberRegex.test(newUserTelephone)) {
          setTelephoneValidationError('Telephone Number must be in numeric value.');
        } else {
          setTelephoneValidationError('');
        }
      }
    }
  };
  const handleUserRoleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (selectedUser) {
      const newRoleName = e.target.value;
      if (!newRoleName) {
        setRoleError('Role is required.');
      } else {
        setRoleError('');
      }
      setSelectedUser({
        ...selectedUser,
        role: newRoleName,
      });
    }
  };
  const handleUserMobileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const newUserMobile = e.target.value.trim();
      setSelectedUser({
        ...selectedUser,
        mobile: newUserMobile,
      });
      if (!newUserMobile) {
        setMobileValidationError('Mobile Number is required.');
      } else {
        const numberRegex = /^[+0-9]+$/;
        if (!numberRegex.test(newUserMobile)) {
          setMobileValidationError('Mobile Number must be in numeric value.');
        } else {
          setMobileValidationError('');
        }
      }
    }
  };
  const handleImageChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    logoType: string,
    MAX_FILE_SIZE: number,
    ALLOWED_TYPES: string[],
    MAX_DIMENSIONS: { [key: string]: { width: number; height: number } }
  ) => {
    const newFile = e.target.files?.[0];
    if (newFile) {
      validateImage(
        newFile,
        logoType,
        () => {
          if (logoType === 'picture') {
            setUserPicture(newFile);
          }
        },
        MAX_FILE_SIZE,
        ALLOWED_TYPES,
        MAX_DIMENSIONS
      );
    }
  };
  const handleStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (selectedUser) {
      const status = parseInt(e.target.value, 10);
      setSelectedUser({
        ...selectedUser,
        status: status,
      });
    }
  };
  const updateUserMutation = useMutation((Data: { id: number; formData: FormData }) => update_user(Data.id, Data.formData), {
    onSuccess: (response) => {
      refetch();
      setLoading(false);
      navigate('/users/users-list');
      toast.success(response.message, {
        position: 'top-right',
        autoClose: 3000,
      });
      if (ref.current) ref.current.value = '';
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'An error occurred', { position: 'top-right', autoClose: 3000 });
      setLoading(false);
    },
  });
  const updateTenantUserMutation = useMutation((Data: { tenantFormData: FormData }) => update_tenant_user(Data.tenantFormData), {
    onSuccess: () => {
      refetch();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'An error occurred with tenant user update.', { position: "top-right", autoClose: 3000 });
    },
  });
 const handleUpdateUser = () => {
  try {
    if (!selectedUser) {
      throw new Error('No User Selected for Update');
    }
    setLoading(true);

    const {
      id,
      title,
      firstname,
      middlename,
      lastname,
      telephone,
      email,
      mobile,
      role,
      address,
      status,
      people_details,
    } = selectedUser;

    const formData: FormData = new FormData();

    
    formData.append('title', title || '');
    formData.append('firstname', firstname || '');
    formData.append('middlename', middlename || '');
    formData.append('lastname', lastname || '');
    formData.append('address', address || '');
    formData.append('mobile', mobile || '');
    formData.append('telephone', telephone || '');
    formData.append('role', role || '');
    formData.append('email', email || '');
    formData.append('status', String(status));
    formData.append('can_get_appointments', '1');

    
    if (people_details) {
      formData.append('address_line_2', people_details.address_line_2 || '');
      formData.append('town', people_details.town || '');
      formData.append('city', people_details.city || '');
      formData.append('postal_code', people_details.postal_code || '');
      formData.append('country', people_details.country || '');
      formData.append('other_information', people_details.other_information || '');
      formData.append('appointment_rate', String(people_details.appointment_rate) || '0');

      
      const categoriesToUpdate = people_details.selected_category_ids;
      if (categoriesToUpdate && typeof categoriesToUpdate === 'object') {
        Object.values(categoriesToUpdate).forEach((cat, index) => {
         
          formData.append(`categories[${index}][id]`, String(cat.id));
        });
      }

      // Sub-categories
      if (people_details.sub_categories && Array.isArray(people_details.sub_categories)) {
        people_details.sub_categories.forEach((subcat, index) => {
          formData.append(`sub_categories[${index}][id]`, String(subcat.id || 0));
          formData.append(`sub_categories[${index}][sub_category_id]`, String(subcat.sub_category_id));
        });
      }

      // People Locations
      if (people_details.people_locations && Array.isArray(people_details.people_locations)) {
        people_details.people_locations.forEach((loc, index) => {
          formData.append(`people_locations[${index}][id]`, String(loc.id || 0));
          formData.append(`people_locations[${index}][location_id]`, String(loc.location_id));
        });
      }

      // People Platforms
      if (people_details.people_platforms && Array.isArray(people_details.people_platforms)) {
        people_details.people_platforms.forEach((plat, index) => {
          formData.append(`people_platforms[${index}][id]`, String(plat.id || 0));
          formData.append(`people_platforms[${index}][location_id]`, String(plat.location_id));
        });
      }
    }

    if (userPicture) {
      formData.append('picture', userPicture);
    }
    
 
    const currentURL = window.location.hostname;
    const frontendDomain = currentURL.replace(/^www\./, '');
    const backendDomain = (
      typeof secureLocalStorage.getItem('baseURL') === 'string'
        ? (secureLocalStorage.getItem('baseURL') as string)
        : ''
    )?.replace(/^(https?:\/\/)/, '');
    const { organization_id, application_id } = organisationApplicationData;
    const tenantFormData: FormData = new FormData();
    tenantFormData.append('first_name', firstname || '');
    tenantFormData.append('middle_name', middlename || '');
    tenantFormData.append('last_name', lastname || '');
    tenantFormData.append('email', email || '');
    tenantFormData.append('mobile_no', mobile || '');
    tenantFormData.append('organization_id', String(organization_id) || '');
    tenantFormData.append('application_id', String(application_id) || '');
    tenantFormData.append('frontend_domain', frontendDomain || '');
    tenantFormData.append('domain', backendDomain || '');
    if (userPicture) {
      tenantFormData.append('profile_picture', userPicture);
    }

    // API calls
    updateTenantUserMutation.mutate({ tenantFormData });
    updateUserMutation.mutate({ id, formData });

  } catch (error: any) {
    console.error("Error building the update payload:", error);
    toast.error(error?.message || 'Data taiyaar karte waqt ek error aayi.', {
      position: 'top-right',
      autoClose: 3000,
    });
    setLoading(false);
  }
};
  const handlePlatformsChange = (selectedOptions: MultiValue<SelectOption>) => {
    if (selectedUser) {
      const updatedPlatforms: PeoplePlatform[] = selectedOptions.map(option => ({
        location_id: parseInt(option.value, 10),
        platform_name: option.label,
      }));
      setSelectedUser({
        ...selectedUser,
        people_details: { ...selectedUser.people_details, people_platforms: updatedPlatforms },
      });
    }
  };
  const handleLocationChange = (selectedOptions: MultiValue<SelectOption>) => {
    if (selectedUser) {
      const updatedLocation: PeopleLocation[] = selectedOptions.map(option => ({
        location_id: parseInt(option.value, 10),
        platform_name: option.label,
      }));
      setSelectedUser({
        ...selectedUser,
        people_details: { ...selectedUser.people_details, people_locations: updatedLocation },
      });
    }
  };
  const handleOtherinfoChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const otherInfo = e.target.value;
      setSelectedUser({
        ...selectedUser,
        people_details: { ...selectedUser.people_details, other_information: otherInfo },
      });
    }
  };
  const handleRateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newRate = e.target.value;
    if (!newRate.trim()) {
      setAppointmentRateError('Please enter an appointment rate.');
    } else if (!/^\d*\.?\d{0,2}$/.test(newRate)) {
      setAppointmentRateError('Please enter a valid amount.');
    }
    else {
      setAppointmentRateError('');
    }
    if (selectedUser) {
      setSelectedUser({
        ...selectedUser,
        people_details: { ...selectedUser.people_details, appointment_rate: parseFloat(newRate) || 0 }
      })
    }
  };
  const handleAddress2Change = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const address2 = e.target.value;
      setSelectedUser({
        ...selectedUser,
        people_details: { ...selectedUser.people_details, address_line_2: address2 },
      });
    }
  };
  const handleTownChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const town = e.target.value;
      if (!town) {
        setTownError('Town is required.');
      } else {
        setTownError('');
      }
      setSelectedUser({
        ...selectedUser,
        people_details: { ...selectedUser.people_details, town: town },
      });
    }
  };
  const handleCityChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const city = e.target.value;
      if (!city) {
        setCityError('City is required.');
      } else {
        setCityError('');
      }
      setSelectedUser({
        ...selectedUser,
        people_details: { ...selectedUser.people_details, city: city },
      });
    }
  };
  const handleCountryChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (selectedUser) {
      const country = e.target.value;
      if (!country) {
        setCountryError('Country is required.');
      } else {
        setCountryError('');
      }
      setSelectedUser({
        ...selectedUser,
        people_details: { ...selectedUser.people_details, country: country },
      });
    }
  };
  const handlePostCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const postCode = e.target.value;
      if (!postCode) {
        setPostCodeError('Post Code is required.');
      } else {
        setPostCodeError('');
      }
      setSelectedUser({
        ...selectedUser,
        people_details: { ...selectedUser.people_details, postal_code: postCode },
      });
    }
  };
  const handleSubcategoryChange = (selectedOptions: MultiValue<SelectOption>) => {
    if (!selectedOptions || selectedOptions.length === 0) {
      setSubcategoryValidationError('Subcategory is required.');
    } else {
      setSubcategoryValidationError('');
    }
    if (selectedUser) {
      const updatedSubcategories: SubCategory[] = selectedOptions.map(option => ({
        sub_category_id: parseInt(option.value, 10),
        sub_category_title: option.label,
      }));
      setSelectedUser({
        ...selectedUser,
        people_details: { ...selectedUser.people_details, sub_categories: updatedSubcategories },
      });
    }
  };
  // Data fetching
 
  const { data: categoryData } = useQuery('categories', get_all_active_categories, { refetchOnWindowFocus: false });
  useEffect(() => {
    if (categoryData) {
      setCategories(categoryData.data as Category[]);
    }
  }, [categoryData]);
  const { data: platformData } = useQuery('platforms', get_all_virtual_platforms, { refetchOnWindowFocus: false });
  useEffect(() => {
    if (platformData) {
      setPlatforms(platformData.data as PeoplePlatform[]);
    }
  }, [platformData]);
  const { data: locationData } = useQuery('locations', get_all_meeting_locations, { refetchOnWindowFocus: false });
  useEffect(() => {
    if (locationData) {
      setLocations(locationData.data as PeopleLocation[]);
    }
  }, [locationData]);
  const ref = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  if (viewIsLoading) {
    return (
      <div className='text-center'>
        <div className='spinner-border' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }
  if (viewIsError) {
    return (
      <div className='text-center'>
        <h2>Error in Get Single Users</h2>
      </div>
    );
  }
 
// const selectedCategories = selectedUser?.people_details?.selected_category_ids;
// const selectedCategoryIds = selectedCategories 
//   ? Object.values(selectedCategories).map(c => c.id) 
//   : [];


// const filteredSubcategories = selectedUser?.people_details?.active_subcategories_against_categories
//   ? Object.values(selectedUser.people_details.active_subcategories_against_categories).filter(subcategory =>
//       selectedCategoryIds.includes(subcategory.categories_id)
//     )
//   : [];


// const selectedSubcategoryIds = selectedUser?.people_details?.sub_categories?.map((subcategory) => subcategory.sub_category_id); 
 
  return (
    <div className='card mb-5 mb-xl-10'>
      <div className='card-header border-0'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>Edit User</span>
        </h3>
        <div className='card-toolbar'>
          <button
            data-toggle="tooltip" data-placement="bottom" title="Navigate to List of Users"
            className='btn btn-sm btn-light-dark fs-5'
            onClick={() => navigate('/users/users-list')}
          >
            List of Users
          </button>
        </div>
      </div>
      <div id='kt_account_profile_details' className='collapse show'>
        <form className='form'>
          <div className='card-body border-top p-9'>
            {selectedUser && selectedUser.people_details ? (
              <>
                {/* Personal Details */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Email</span>
                  </label>
                  <div className='col-lg-8 fv-row'>
                    <label className='form-control form-control-lg form-control-solid user-input'>
                      {selectedUser.email}
                    </label>
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Role</span>
                  </label>
                  <div className='col-lg-8 fv-row'>
                    <select
                      className='form-select form-select-solid form-select-lg fw-bold'
                      value={selectedUser.role || ''}
                      onChange={handleUserRoleChange}
                    >
                      <option value=''>Select a Role...</option>
                      {Array.isArray(roleList) && roleList.length > 0 ? (
                        roleList.map((role) => (
                          <option key={role.id} value={role.name}>
                            {role.name}
                          </option>
                        ))
                      ) : (
                        <option disabled>No roles available</option>
                      )}
                    </select>
                    {roleError && <div className='text-danger'>{roleError}</div>}
                  </div>
                </div>
                 <div className='row mb-6'>
                <label className='col-lg-4 col-form-label fw-bold fs-6'>
                  <span className='required'>Category</span>
                </label>
                <div className='col-lg-8 fv-row'>
                  <Select
                    isMulti
                    placeholder="Select Categories..."
                    // 'options' ke liye hum 'categories' state ka istemal karenge jo API se aa rahi hai
                    options={categories.map((category) => ({
                      value: String(category.id),
                      label: category.title,
                    }))}
                    // 'value' ke liye user ki pehle se chuni hui categories dikhayein
                    value={
                      selectedUser?.people_details?.selected_category_ids
                        ? Object.values(selectedUser.people_details.selected_category_ids).map((cat) => ({
                            value: String(cat.id),
                            label: cat.title,
                          }))
                        : []
                    }
                    className="basic-multi-select"
                    classNamePrefix="select"
                    onChange={(selectedOptions) => {
                      // Jab user selection badle, to state ko update karein
                      const updatedCategories = selectedOptions.map((option) => ({
                        id: parseInt(option.value, 10),
                        title: option.label,
                      }));
                      if (selectedUser) {
                        setSelectedUser({
                          ...selectedUser,
                          people_details: {
                            ...selectedUser.people_details,
                            selected_category_ids: updatedCategories,
                          },
                        });
                      }
                    }}
                  />
                </div>
              </div>

                {/* Subcategory */}
              <div className='row mb-6'>
    <label className='col-lg-4 col-form-label fw-bold fs-6'>
        <span className='required'>Subcategory</span>
    </label>
    <div className='col-lg-8 fv-row'>
        <Select
            isMulti
            placeholder={filteredSubcategories.length ? "Select Subcategory" : "Please select a category first"}
            className="basic-multi-select"
            classNamePrefix="select"
            
            // FINAL FIX: Filter karein AUR fallback value dein.
            options={filteredSubcategories
                // Step 1: Sirf unko rakhein jinka ID aur Title dono maujood hain.
                .filter(sub => sub && sub.id && sub.title)
                // Step 2: Map karte waqt, title ke liye ek fallback dein.
                .map(sub => ({
                    value: String(sub.id),
                    // YEH LINE ERROR FIX KAREGI: Agar title undefined ho, to khaali string istemal karein.
                    label: sub.title ?? '', 
                }))
            }
            
            value={
                selectedUser?.people_details?.sub_categories?.map(selected => ({
                    value: String(selected.sub_category_id),
                    label: selected.sub_category_title
                })) || []
            }
            
            onChange={(selectedOptions) => {
                const updatedSubcategories = selectedOptions.map(option => ({
                    sub_category_id: parseInt(option.value, 10),
                    sub_category_title: option.label,
                }));
                if (selectedUser) {
                    setSelectedUser({
                        ...selectedUser,
                        people_details: { ...selectedUser.people_details, sub_categories: updatedSubcategories },
                    });
                }
            }}
        />
        {subcategoryValidationError && <div className='text-danger'>{subcategoryValidationError}</div>}
    </div>
</div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Title</span>
                  </label>
                  <div className='col-lg-8 fv-row'>
                    <select
                      className='form-select form-select-solid form-select-lg fw-bold'
                      value={selectedUser.title || ''}
                      onChange={handleTitleChange}
                    >
                      <option value=''>Select a Title...</option>
                      <option value='Mr.'>Mr.</option>
                      <option value='Mrs.'>Mrs.</option>
                      <option value='Ms.'>Ms.</option>
                    </select>
                    {titleError && <div className='text-danger'>{titleError}</div>}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label required fw-bold fs-6'>First Name</label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='First Name'
                      value={selectedUser.firstname || ''}
                      onChange={handleFirstNameChange}
                    />
                    {firstNameError && <div className='text-danger'>{firstNameError}</div>}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>Middle Name</label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Middle Name'
                      value={selectedUser.middlename || ''}
                      onChange={handleMidddleNameChange}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label required fw-bold fs-6'>Last Name</label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Last Name'
                      value={selectedUser.lastname || ''}
                      onChange={handleLastNameChange}
                    />
                  </div>
                </div>
                {/* Address Details */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>Address Line 1</label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Address Line 1'
                      value={selectedUser.address || ''}
                      onChange={handleAddressChange}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>Address Line 2</label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Address Line 2'
                      value={selectedUser.people_details.address_line_2 || ''}
                      onChange={handleAddress2Change}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6 required'>Town</label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Town'
                      value={selectedUser.people_details.town || ''}
                      onChange={handleTownChange}
                    />
                    {townError && <div className='text-danger'>{townError}</div>}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6 required'>City</label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='City'
                      value={selectedUser.people_details.city || ''}
                      onChange={handleCityChange}
                    />
                    {cityError && <div className='text-danger'>{cityError}</div>}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6 required'>Country</label>
                  <div className='col-lg-8 fv-row'>
                    <select
                      className='form-select form-select-lg form-select-solid'
                      value={selectedUser.people_details.country || ''}
                      onChange={handleCountryChange}
                    >
                      <option value=''>Select a country...</option>
                      {getCountries().map((country, index) => (
                        <option key={index} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                    {countryError && <div className='text-danger'>{countryError}</div>}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6 required'>Postal Code</label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Postal Code'
                      value={selectedUser.people_details.postal_code || ''}
                      onChange={handlePostCodeChange}
                    />
                    {postCodeError && <div className='text-danger'>{postCodeError}</div>}
                  </div>
                </div>
                {/* Contact and Other Info */}
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>Telephone</label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='tel'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Telephone'
                      value={selectedUser.telephone || ''}
                      onChange={handleTelephoneChange}
                    />
                    {telephoneValidationError && <div className='text-danger'>{telephoneValidationError}</div>}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>
                    <span className='required'>Mobile number</span>
                  </label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='tel'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Mobile number'
                      value={selectedUser.mobile || ''}
                      onChange={handleUserMobileChange}
                    />
                    {mobileValidationError && <div className='text-danger'>{mobileValidationError}</div>}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>Appointment Rate ({selectedUser.people_details.currency?.currency_symbol})</label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='e.g. 50.00'
                      value={selectedUser.people_details.appointment_rate || ''}
                      onChange={handleRateChange}
                    />
                    {appointmentRateError && <div className='text-danger'>{appointmentRateError}</div>}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>Available Platforms</label>
                  <div className='col-lg-8 fv-row'>
                    <Select
                      isMulti
                      options={platforms.map(p => ({ value: String(p.id), label: p.platform_name }))}
                      value={selectedUser.people_details.people_platforms.map(p => ({ value: String(p.location_id), label: p.platform_name }))}
                      onChange={handlePlatformsChange}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder="Select Platforms..."
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>Available Locations</label>
                  <div className='col-lg-8 fv-row'>
                    <Select
                      isMulti
                      options={locations.map(l => ({ value: String(l.id), label: l.platform_name }))}
                      value={selectedUser.people_details.people_locations.map(l => ({ value: String(l.location_id), label: l.platform_name }))}
                      onChange={handleLocationChange}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      placeholder="Select Locations..."
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>Other Information</label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='text'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Other Information'
                      value={selectedUser.people_details.other_information || ''}
                      onChange={handleOtherinfoChange}
                    />
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>Picture</label>
                  <div className='col-lg-8 fv-row'>
                    <input
                      type='file'
                      className='form-control form-control-lg form-control-solid'
                      onChange={(e) => handleImageChange(e, 'picture', 2, ['image/jpeg', 'image/jpg', 'image/png'], { picture: { width: 200, height: 200 } })}
                      accept="image/jpeg, image/jpg, image/png"
                      name='picture'
                      ref={ref}
                    />
                    <div className='text-danger mx-6 mt-2'>
                      Recommended size: 200 x 200 px, max file size: 2MB, and accepted file types: jpg, jpeg, or png only.
                    </div>
                    {selectedUser.picture && (
                      <div className='mt-2'>
                        <img
                          src={selectedUser.picture}
                          alt='User Picture'
                          style={{ maxWidth: '100%', maxHeight: '150px' }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className='row mb-6'>
                  <label className='col-lg-4 col-form-label fw-bold fs-6'>Status</label>
                  <div className='col-lg-8 fv-row'>
                    <Form.Select
                      className='form-select form-select-solid form-select-lg fw-bold'
                      value={selectedUser.status}
                      onChange={handleStatusChange}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </Form.Select>
                  </div>
                </div>
              </>
            ) : null}
          </div>
          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button
              type='button'
              className='btn btn-primary'
              onClick={handleUpdateUser}
              disabled={loading || !!firstNameError || !!roleError || !!titleError || !!mobileValidationError || !!postCodeError || !!countryError || !!cityError || !!townError}
            >
              {!loading && 'Update User'}
              {loading && (
                <span className='indicator-progress d-block'>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
};
export default EditUser;